/**
 * BulkUpload.jsx
 * Many pictures at once, in whatever shape and format they arrive in.
 *
 * The number of files was never the hard part — the picker has taken several
 * for a while. What stopped this working was that a PNG uploads perfectly
 * well, sits in the library looking fine, and is refused by Instagram at
 * publish time; and so is anything outside a narrow range of shapes. Both
 * failures happen at the worst possible moment, days later, on a schedule.
 *
 * So every file is examined before anything is sent, and the list says exactly
 * what will be done to each one — converted, padded, cropped, resized, or left
 * alone. Nothing is silently altered, and a picture that is already fine is
 * uploaded untouched rather than re-encoded for the sake of consistency.
 *
 * Uploads run a few at a time. All forty at once would have the browser open
 * forty connections and the Worker handle forty multipart bodies, for no gain
 * over a steady four.
 */

import { useEffect, useRef, useState } from 'react';
import { api } from '../services/api.js';
import {
  FIT_MODES,
  describePlan,
  isReadable,
  planConversion,
  prepareForInstagram,
  readImage,
  shortType,
} from '../utils/image.js';
import { Upload, Check, Warning, Image as ImageIcon, Clipboard } from './Icons.jsx';
import { filesFromDrop } from '../utils/zip.js';
import { filesFromClipboard, readClipboardImages } from '../utils/clipboard.js';

/** At a time. Enough to be quick, few enough to stay polite. */
const CONCURRENCY = 4;

/**
 * @param {object} props
 * @param {string} props.brandId
 * @param {(added: Array) => void} [props.onDone] - the media rows created
 */
export default function BulkUpload({ brandId, onDone }) {
  const [items, setItems] = useState([]);
  const [fit, setFit] = useState('pad');
  const [background, setBackground] = useState('#ffffff');
  const [busy, setBusy] = useState(false);
  const [dropping, setDropping] = useState(0);
  const [error, setError] = useState(null);
  const input = useRef(null);
  const folderInput = useRef(null);

  // webkitdirectory is not in React's attribute list, so it is set by hand.
  // Both spellings, because older builds still want the unprefixed one.
  useEffect(() => {
    if (folderInput.current) {
      folderInput.current.setAttribute('webkitdirectory', '');
      folderInput.current.setAttribute('directory', '');
    }
  }, []);

  /** Reads each file far enough to say what will happen to it. */
  async function examine(files) {
    setError(null);
    const list = [];

    for (const file of Array.from(files ?? [])) {
      if (!file.type.startsWith('image/')) {
        list.push({ file, state: 'rejected', reason: `${file.name} is not an image.` });
        continue;
      }
      if (!isReadable(file.type)) {
        // HEIC from an iPhone is the usual one. Nothing here can decode it.
        list.push({
          file,
          state: 'rejected',
          reason:
            `${file.name} is a ${shortType(file.type)}. Browsers cannot convert that — export it ` +
            'as JPEG or PNG first.',
        });
        continue;
      }

      try {
        const { bitmap, width, height } = await readImage(file);
        bitmap.close?.();
        list.push({
          file,
          state: 'ready',
          width,
          height,
          plan: planConversion({ width, height, type: file.type }, { fit }),
        });
      } catch (e) {
        list.push({ file, state: 'rejected', reason: e.message });
      }
    }

    setItems(list);
  }

  /** Re-plans everything when the fit changes, without re-reading the files. */
  function changeFit(next) {
    setFit(next);
    setItems((current) =>
      current.map((item) =>
        item.state === 'ready' && item.width
          ? {
              ...item,
              plan: planConversion(
                { width: item.width, height: item.height, type: item.file.type },
                { fit: next },
              ),
            }
          : item,
      ),
    );
  }

  async function upload() {
    setBusy(true);
    setError(null);

    const queue = items
      .map((item, index) => ({ item, index }))
      .filter(({ item }) => item.state === 'ready');

    const added = [];
    let cursor = 0;

    // A small pool rather than Promise.all over everything: forty at once is
    // forty connections and forty multipart bodies for no gain over four.
    async function worker() {
      while (cursor < queue.length) {
        const { item, index } = queue[cursor++];
        setItems((c) => c.map((x, i) => (i === index ? { ...x, state: 'working' } : x)));

        try {
          const { file, changed } = await prepareForInstagram(item.file, { fit, background });
          const res = await api.media.upload(brandId, file);

          added.push(res.media);
          setItems((c) =>
            c.map((x, i) =>
              i === index ? { ...x, state: 'done', changed, media: res.media } : x,
            ),
          );
        } catch (e) {
          setItems((c) =>
            c.map((x, i) => (i === index ? { ...x, state: 'failed', reason: e.message } : x)),
          );
        }
      }
    }

    try {
      await Promise.all(Array.from({ length: CONCURRENCY }, worker));
      onDone?.(added);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  function reset() {
    setItems([]);
    setError(null);
    if (input.current) input.current.value = '';
  }

  /**
   * The Paste button.
   *
   * Offered as well as Ctrl+V because Ctrl+V needs the page to have keyboard
   * focus, and that is exactly what has gone if you have just come back from a
   * screenshot tool.
   */
  async function pasteFromClipboard() {
    const { files, reason } = await readClipboardImages();
    if (reason) {
      setError(new Error(reason));
      return;
    }
    setError(null);
    examine(files);
  }

  // A paste lands on the window rather than on any element, so this listens
  // globally. Pasted text is somebody else's business and is ignored — there
  // are no files on the clipboard in that case.
  useEffect(() => {
    function onPaste(e) {
      if (!brandId) return;
      const files = filesFromClipboard(e.clipboardData);
      if (files.length === 0) return;

      e.preventDefault();
      examine(files);
    }

    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
    // examine reads current state through closures each render, which is why
    // the listener is re-bound rather than registered once.
  });

  const dragProps = {
    onDragEnter: (e) => {
      if (!e.dataTransfer?.types?.includes('Files')) return;
      e.preventDefault();
      setDropping((d) => d + 1);
    },
    onDragOver: (e) => {
      if (e.dataTransfer?.types?.includes('Files')) e.preventDefault();
    },
    onDragLeave: () => setDropping((d) => Math.max(0, d - 1)),
    onDrop: async (e) => {
      e.preventDefault();
      setDropping(0);

      // The hint has promised folders for a while; this is what makes it true.
      // A dropped folder has no `files` — it arrives as a FileSystemEntry that
      // has to be walked, the same way the Resize screen does it.
      const found = await filesFromDrop(e.dataTransfer?.items ?? [], { maxFiles: 200 });
      examine(found.length ? found : e.dataTransfer?.files);
    },
  };

  const ready = items.filter((i) => i.state === 'ready').length;
  const changing = items.filter((i) => i.state === 'ready' && i.plan?.needed).length;
  const uploaded = items.filter((i) => i.state === 'done').length;
  const finished = uploaded > 0 && ready === 0;

  return (
    <section className={`card dropzone${dropping > 0 ? ' is-dropping' : ''}`} {...dragProps}>
      <div className="card__head">
        <span className="tile">
          <Upload size={21} />
        </span>
        <div className="card__head-text">
          <h2>Add pictures</h2>
          <p>
            As many as you like, any format, any shape. Anything Instagram would refuse is fixed
            before it is uploaded, and you are told what changed.
          </p>
        </div>
      </div>

      {error && (
        <div className="notice notice--error">
          <Warning />
          <span>{error.message}</span>
        </div>
      )}

      {items.length === 0 ? (
        <div className="field">
          <label htmlFor="bulk-files">Pictures</label>
          <input
            id="bulk-files"
            ref={input}
            type="file"
            multiple
            accept="image/jpeg,image/png,image/webp,image/avif,image/gif"
            disabled={!brandId}
            onChange={(e) => examine(e.target.files)}
          />
          <div className="btn-row" style={{ marginTop: 10 }}>
            <button
              className="btn btn--sm"
              onClick={() => folderInput.current?.click()}
              disabled={!brandId}
            >
              <Upload size={15} /> Choose a folder
            </button>
            <button
              className="btn btn--sm"
              onClick={pasteFromClipboard}
              disabled={!brandId}
              title="Paste a picture you have copied"
            >
              <Clipboard size={15} /> Paste
            </button>
          </div>

          <input
            ref={folderInput}
            type="file"
            multiple
            hidden
            onChange={(e) => {
              // A folder hands over everything inside it, Thumbs.db included.
              // Those are dropped quietly — naming them would be noise.
              const all = Array.from(e.target.files ?? []).filter((f) =>
                f.type.startsWith('image/'),
              );
              examine(all);
              e.target.value = '';
            }}
          />

          <p className="hint">
            {brandId
              ? 'Hold Ctrl (or Cmd) to pick several at once. You can also drag files or a whole folder onto this panel, or press Ctrl+V to paste one. Nothing is uploaded yet.'
              : 'Choose a brand first.'}
          </p>
        </div>
      ) : (
        <>
          {/* ---------- how odd shapes are handled ---------- */}
          {!busy && !finished && changing > 0 && (
            <div className="field">
              <label>Pictures that are the wrong shape</label>
              <div className="btn-row">
                {Object.entries(FIT_MODES).map(([key, m]) => (
                  <button
                    key={key}
                    type="button"
                    className={`btn btn--sm ${fit === key ? 'btn--primary' : ''}`}
                    onClick={() => changeFit(key)}
                  >
                    {m.label}
                  </button>
                ))}
                {fit === 'pad' && (
                  <input
                    type="color"
                    value={background}
                    onChange={(e) => setBackground(e.target.value)}
                    title="The colour of the border"
                    style={{ width: 44, padding: 2 }}
                  />
                )}
              </div>
              <p className="hint">{FIT_MODES[fit].hint}</p>
            </div>
          )}

          <div className="importrows">
            {items.map((item, i) => (
              <FileRow key={`${item.file.name}-${i}`} item={item} />
            ))}
          </div>

          <div className="btn-row" style={{ marginTop: 14 }}>
            {!finished && (
              <button className="btn btn--primary" onClick={upload} disabled={busy || ready === 0}>
                <Check />
                {busy
                  ? 'Uploading…'
                  : `Upload ${ready} picture${ready === 1 ? '' : 's'}` +
                    (changing > 0 ? ` (${changing} will be adjusted)` : '')}
              </button>
            )}
            <button className="btn" onClick={reset} disabled={busy}>
              {finished ? 'Add more' : 'Start again'}
            </button>
          </div>
        </>
      )}
    </section>
  );
}

function FileRow({ item }) {
  const bad = item.state === 'rejected' || item.state === 'failed';

  return (
    <div className={`importrow${bad ? ' importrow--bad' : ''}`}>
      <span className="importrow__line">
        {item.state === 'done' ? <Check size={13} /> : <ImageIcon size={13} />}
      </span>

      <div className="importrow__body">
        <div className="importrow__top">
          <span className="badge">{item.file.name}</span>
          {item.width && (
            <span className="badge">
              {item.width}×{item.height}
            </span>
          )}
          {item.state === 'working' && <span className="badge badge--accent">uploading…</span>}
          {item.state === 'done' && (
            <span className="badge badge--ok">{item.changed ? 'adjusted and added' : 'added'}</span>
          )}
        </div>

        {bad ? (
          <p className="importrow__problem">
            <Warning size={13} /> {item.reason}
          </p>
        ) : (
          <p className="importrow__caption">{item.plan ? describePlan(item.plan) : ''}</p>
        )}
      </div>
    </div>
  );
}
